import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Plus, Trash2 } from "lucide-react";
import { ujmsSpindleConfig, ujmsSaveSpindleConfig } from "../../../services/ujmsApi";
import { cls, Field, inputCls, selectCls, btnGhost, btnPrimary, Stat } from "../../ui";
import { SectionHdr } from "../SummaryTab";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function RuleEditor({ rule, onChange, testId }) {
  const set = (k, v) => onChange({ ...rule, [k]: v });
  const toggleDay = (i) => set("days", rule.days.includes(i) ? rule.days.filter((d) => d !== i) : [...rule.days, i].sort());
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11.5px]" data-testid={testId}>
      <Field label="Call days">
        <div className="flex gap-1 flex-wrap">{DAYS.map((d, i) => <button key={d} type="button" onClick={() => toggleDay(i)} data-testid={`${testId}-day-${i}`} className={cls("border px-1.5 py-[2px] rounded-sm text-[10.5px] font-semibold", rule.days.includes(i) ? "bg-ujms-dark text-white border-ujms-dark" : "bg-white border-slate-300 text-slate-600")}>{d}</button>)}</div>
      </Field>
      <Field label="Call starts"><input type="time" className={inputCls} value={rule.start} onChange={(e) => set("start", e.target.value)} data-testid={`${testId}-start`} /></Field>
      <Field label="Call ends"><input type="time" className={inputCls} value={rule.end} onChange={(e) => set("end", e.target.value)} data-testid={`${testId}-end`} /></Field>
      <Field label="Slot length (min)"><input type="number" className={inputCls} value={rule.slot_minutes} onChange={(e) => set("slot_minutes", Number(e.target.value))} data-testid={`${testId}-slot`} /></Field>
      <Field label="Motions per slot"><input type="number" className={inputCls} value={rule.capacity} onChange={(e) => set("capacity", Number(e.target.value))} data-testid={`${testId}-capacity`} /></Field>
      <Field label="Minimum notice (days)"><input type="number" className={inputCls} value={rule.min_notice_days} onChange={(e) => set("min_notice_days", Number(e.target.value))} data-testid={`${testId}-notice`} /></Field>
      <Field label="Booking horizon (days)"><input type="number" className={inputCls} value={rule.horizon_days} onChange={(e) => set("horizon_days", Number(e.target.value))} data-testid={`${testId}-horizon`} /></Field>
      <Field label="Hearing duration (min)"><input type="number" className={inputCls} value={rule.duration_minutes} onChange={(e) => set("duration_minutes", Number(e.target.value))} data-testid={`${testId}-duration`} /></Field>
    </div>
  );
}

/** Administration › Motion spindle — court-wide default call plus per-courtroom overrides. */
export function MotionSpindleAdmin() {
  const [cfg, setCfg] = useState(null);
  const [def, setDef] = useState(null);
  const [rooms, setRooms] = useState({});
  const [addRoom, setAddRoom] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => ujmsSpindleConfig().then((r) => { setCfg(r.data); setDef(r.data.default); setRooms(r.data.courtrooms || {}); }).catch((e) => toast.error(e.response?.data?.detail || "Spindle config unavailable"));
  useEffect(() => { load(); }, []);
  if (!cfg || !def) return <div className="text-[11px] text-slate-400 p-2">Loading…</div>;
  const save = async () => {
    setBusy(true);
    try { await ujmsSaveSpindleConfig({ default: def, courtrooms: rooms }); toast.success("Motion spindle rules saved"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    setBusy(false);
  };
  const roomName = (id) => cfg.rooms.find((r) => r.id === id)?.name || id;
  const unused = cfg.rooms.filter((r) => !rooms[r.id]);
  return (
    <div className="space-y-1.5" data-testid="spindle-admin">
      <div className="grid grid-cols-3 gap-1">
        <Stat label="Motions spindled (30 days)" value={cfg.bookings_30d} testId="spindle-kpi-bookings" />
        <Stat label="Courtroom overrides" value={Object.keys(rooms).length} testId="spindle-kpi-rooms" />
        <Stat label="Default capacity / slot" value={def.capacity} testId="spindle-kpi-capacity" />
      </div>
      <section className="ent-panel">
        <SectionHdr title="Court-wide motion call (default spindle)" right={<span className="italic font-normal">{def.authority}</span>} />
        <div className="p-2"><RuleEditor rule={def} onChange={setDef} testId="spindle-default" /></div>
      </section>
      <section className="ent-panel">
        <SectionHdr title="Per-courtroom overrides" right={
          <span className="inline-flex items-center gap-1">
            <select className={cls(selectCls, "!w-56")} value={addRoom} onChange={(e) => setAddRoom(e.target.value)} data-testid="spindle-add-room"><option value="">Add a courtroom…</option>{unused.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select>
            <button className={btnGhost} disabled={!addRoom} onClick={() => { setRooms({ ...rooms, [addRoom]: { ...def } }); setAddRoom(""); }} data-testid="spindle-add-room-btn"><Plus className="w-3 h-3" /> Override</button>
          </span>} />
        <div className="p-2 space-y-2">
          {Object.entries(rooms).map(([rid, r]) => (
            <div key={rid} className="border border-slate-200 p-1.5" data-testid={`spindle-room-${rid}`}>
              <div className="flex items-center justify-between mb-1"><span className="font-semibold text-[12px] text-ujms-deep">{roomName(rid)}</span><button className={btnGhost} onClick={() => { const n = { ...rooms }; delete n[rid]; setRooms(n); }} data-testid={`spindle-room-remove-${rid}`}><Trash2 className="w-3 h-3" /> Remove</button></div>
              <RuleEditor rule={{ ...def, ...r }} onChange={(v) => setRooms({ ...rooms, [rid]: v })} testId={`spindle-room-rule-${rid}`} />
            </div>
          ))}
          {!Object.keys(rooms).length && <div className="italic text-[11px] text-slate-400">Every courtroom follows the court-wide default.</div>}
        </div>
      </section>
      <div className="flex justify-end"><button className={btnPrimary} disabled={busy} onClick={save} data-testid="spindle-save"><Save className="w-3 h-3" /> Save spindle rules</button></div>
    </div>
  );
}
